import { useEffect, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Container, Box, Title, Loader, Center, Alert, ScrollArea,
  Paper, Group, ActionIcon, Text, Badge, Stack, Button,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { ArrowLeft, CheckCircle2 } from 'lucide-react'
import { RichText } from '../../components/common/RichText'
import { contentApi, type Course } from '../../api/content'
import api from '../../api/client'

export function CourseViewer() {
  const { slug } = useParams<{ slug: string }>()
  const [searchParams] = useSearchParams()
  const assignmentId = searchParams.get('assignmentId')
  const navigate = useNavigate()
  const [course, setCourse] = useState<Course | null>(null)
  const [content, setContent] = useState<string | null>(null)
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [completing, setCompleting] = useState(false)

  useEffect(() => {
    if (!slug) return

    let isMounted = true

    const loadCourse = async () => {
      setLoading(true)
      setError(false)

      try {
        const courseResponse = await contentApi.getCourse(slug)
        const courseData = courseResponse.data
        const markdownResponse = await fetch(`/content/${courseData.course_file}.md`)
        if (!markdownResponse.ok) {
          throw new Error('Not found')
        }

        const markdown = await markdownResponse.text()
        if (!isMounted) return

        setCourse(courseData)
        setContent(markdown)

        // Mark assignment as processing when page loads
        if (assignmentId) {
          api.post(`/assignments/${assignmentId}/start`).catch(() => {})
        }
      } catch {
        if (!isMounted) return
        setError(true)
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    void loadCourse()

    return () => {
      isMounted = false
    }
  }, [slug, assignmentId])

  function handleComplete() {
    if (!assignmentId) return
    setCompleting(true)
    api.post(`/assignments/${assignmentId}/complete`)
      .then(() => {
        notifications.show({ title: 'Completed', message: 'Course marked as completed', color: 'green' })
        navigate('/study-plan')
      })
      .catch(() => notifications.show({ title: 'Error', message: 'Failed to complete assignment', color: 'red' }))
      .finally(() => setCompleting(false))
  }

  if (loading) return <Center h={400}><Loader size="lg" /></Center>
  if (error) return (
    <Container size="md" py="xl">
      <Alert color="red" title="Course not found">
        The course "{slug}" could not be loaded.
      </Alert>
    </Container>
  )

  return (
    <Box w="90%" py="xl" mx="auto">
      <Group mb="md">
        <ActionIcon variant="subtle" onClick={() => navigate('/study-plan')}>
          <ArrowLeft size={20} />
        </ActionIcon>
        <Text style={{ cursor: 'pointer' }} size="sm" c="dimmed" onClick={() => navigate('/study-plan')}>Back to Learning Plan</Text>
      </Group>
      <Paper p="xl" radius="md" withBorder>
        {course && (
          <Stack gap="md" mb="lg">
            <Group justify="space-between" align="flex-start">
              <Title order={2}>{course.name}</Title>
              <Badge variant="light">{course.domain_name}</Badge>
            </Group>
            {course.description && (
              <Text c="dimmed">{course.description}</Text>
            )}
          </Stack>
        )}
        <ScrollArea>
          <RichText size="lg">{content}</RichText>
        </ScrollArea>
      </Paper>

      {assignmentId && (
        <Group justify="center" mt="xl">
          <Button
            size="lg"
            radius="xl"
            color="green"
            leftSection={<CheckCircle2 size={20} />}
            loading={completing}
            onClick={handleComplete}
          >
            Complete
          </Button>
        </Group>
      )}
    </Box>
  )
}
